<template>
  <video
    ref="video"
    class="compatible-video"
    :class="videoClass"
    :controls="controls"
    :autoplay="autoplay"
    :muted="muted"
    :loop="loop"
    :preload="preload"
    playsinline
    webkit-playsinline
    x5-playsinline
    x5-video-player-type="h5"
    x5-video-player-fullscreen="true"
    :controlslist="controlsList"
    @loadedmetadata="onMeta"
    @loadeddata="onLoadedData"
    @play="onPlay"
    @error="onError"
  >
    <source
      v-for="(item, idx) in activeSources"
      :key="item.src + '-' + idx"
      :src="item.src"
      :type="item.type"
    >
  </video>
</template>

<script>
import { resolveVideoPlaySources, guessVideoMime } from '@/utils/videoPlay'

export default {
  name: 'CompatibleVideo',
  props: {
    /** Original CMS/OSS video URL */
    src: { type: String, default: '' },
    /** Optional pre-resolved sources [{src,type}] ?? skips API resolve */
    sources: { type: Array, default: null },
    videoClass: { type: [String, Object, Array], default: '' },
    controls: { type: Boolean, default: true },
    autoplay: { type: Boolean, default: false },
    muted: { type: Boolean, default: false },
    loop: { type: Boolean, default: false },
    preload: { type: String, default: 'metadata' },
    controlsList: { type: String, default: 'nodownload' },
    /** Seek slightly forward so first frame paints (no #t= media fragment) */
    showFirstFrame: { type: Boolean, default: true }
  },
  data() {
    return {
      resolved: [],
      sourceIndex: 0,
      triedFallback: false
    }
  },
  computed: {
    activeSources() {
      if (this.sources && this.sources.length) {
        return this.sources.slice(this.sourceIndex)
      }
      return this.resolved.slice(this.sourceIndex)
    }
  },
  watch: {
    src: {
      immediate: true,
      handler(val) {
        if (this.sources && this.sources.length) return
        this.loadSources(val)
      }
    },
    sources: {
      immediate: true,
      handler(list) {
        if (list && list.length) {
          this.resolved = []
          this.sourceIndex = 0
          this.triedFallback = false
          this.$nextTick(() => this.reloadVideo())
        }
      }
    }
  },
  mounted() {
    this.applyCompatAttrs()
  },
  methods: {
    applyCompatAttrs() {
      const el = this.$refs.video
      if (!el) return
      // Extra attrs for QQ / WeChat / older WebKit (setAttribute is more reliable than Vue boolean attrs)
      ;[
        ['playsinline', 'true'],
        ['webkit-playsinline', 'true'],
        ['x5-playsinline', 'true'],
        ['x5-video-player-type', 'h5'],
        ['x5-video-player-fullscreen', 'true'],
        ['x5-video-orientation', 'portrait']
      ].forEach(([k, v]) => el.setAttribute(k, v))
    },

    async loadSources(raw) {
      this.sourceIndex = 0
      this.triedFallback = false
      if (!raw) {
        this.resolved = []
        return
      }
      try {
        this.resolved = await resolveVideoPlaySources(raw)
      } catch (e) {
        this.resolved = [{ src: raw, type: guessVideoMime(raw) }]
      }
      this.$nextTick(() => this.reloadVideo())
    },

    reloadVideo() {
      const el = this.$refs.video
      if (!el) return
      try {
        el.load()
        if (this.autoplay) {
          const p = el.play()
          if (p && typeof p.catch === 'function') p.catch(() => {})
        }
      } catch (e) { /* ignore */ }
    },

    paintFirstFrame(el) {
      if (!this.showFirstFrame || !el) return
      try {
        // Prefer JS seek over media fragment (#t=0.001), which breaks Edge/QQ Range seeking
        if (el.readyState >= 2 && el.currentTime < 0.05) {
          const t = Math.min(0.1, (el.duration && isFinite(el.duration) ? el.duration * 0.01 : 0.1))
          el.currentTime = t > 0 ? t : 0.01
        }
      } catch (e) { /* ignore */ }
    },

    onMeta(e) {
      this.$emit('loadedmetadata', e)
      this.paintFirstFrame(e && e.target)
    },

    onLoadedData(e) {
      this.$emit('loadeddata', e)
      this.paintFirstFrame(e && e.target)
    },

    onPlay(e) {
      this.$emit('play', e)
    },

    onError(e) {
      const list = (this.sources && this.sources.length) ? this.sources : this.resolved
      if (this.sourceIndex < list.length - 1) {
        this.sourceIndex += 1
        this.$nextTick(() => this.reloadVideo())
        return
      }
      this.$emit('error', e)
    }
  }
}
</script>

<style scoped>
.compatible-video {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  background: #0f172a;
  /* Avoid GPU compositing quirks that cause ??audio only / black frame?? on Edge & X5 */
  transform: none;
  -webkit-transform: none;
  backface-visibility: visible;
  -webkit-backface-visibility: visible;
}
</style>
